import {
    OVERLAY_DEFAULT_CONFIG,
    type OverlayDefaultConfig,
} from '@angular/cdk/overlay';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptors,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    ErrorHandler,
    provideAppInitializer,
    provideZoneChangeDetection,
    type ApplicationConfig,
    type EnvironmentProviders,
    type Provider,
} from '@angular/core';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_CARD_CONFIG, type MatCardConfig } from '@angular/material/card';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {
    MAT_FORM_FIELD_DEFAULT_OPTIONS,
    type MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import {
    MAT_TOOLTIP_DEFAULT_OPTIONS,
    type MatTooltipDefaultOptions,
} from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    provideRouter,
    TitleStrategy,
    withComponentInputBinding,
    withExperimentalAutoCleanupInjectors,
    withPreloading,
    withRouterConfig,
} from '@angular/router';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { provideIcons } from '@ng-icons/core';
import { provideNgIdle } from '@ng-idle/core';
import {
    APP_CONFIG_TOKEN,
    buildUiProfileConfig,
    type Environment,
} from '@quipex/core/config';
import {
    ManageHttpInterceptor,
    SelectivePreloadingStrategy,
    apiBaseUrlInterceptor,
} from '@quipex/core/http';
import { GlobalErrorHandler, MonitoringService } from '@quipex/core/logging';
import { DialogService } from '@quipex/shared/ui';
import { QPX_LUCIDE_ICONS } from '@quipex/shared/ui/icons';
import { enAU } from 'date-fns/locale';
import { environment } from '../environments/environment';
import { productionFeatureFlags } from '../environments/feature-flags.production';
import { validateEnvironmentAndInitialiseAppFn } from './app-initialisation';
import { QuipexTitleStrategy } from './app-title-strategy';
import { appRoutes } from './app.routes';

const matAngularConfig: (Provider | EnvironmentProviders)[] = [
    {
        provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
        useValue: {
            appearance: 'outline',
        } satisfies MatFormFieldDefaultOptions,
    },
    {
        provide: MAT_CARD_CONFIG,
        useValue: { appearance: 'outlined' } satisfies MatCardConfig,
    },
    {
        provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
        useValue: {
            showDelay: 500,
            hideDelay: 0,
            touchendHideDelay: 1500,
        } satisfies MatTooltipDefaultOptions,
    },
    {
        provide: OVERLAY_DEFAULT_CONFIG,
        useValue: { usePopover: false } satisfies OverlayDefaultConfig,
    },
    provideDateFnsAdapter({
        parse: {
            dateInput: 'dd/MM/yyyy',
        },
        display: {
            dateInput: 'dd/MM/yyyy',
            monthYearLabel: 'MMM yyyy',
            dateA11yLabel: 'PP',
            monthYearA11yLabel: 'MMMM yyyy',
        },
    }),
    { provide: MAT_DATE_LOCALE, useValue: enAU },
    DialogService,
    provideIcons(QPX_LUCIDE_ICONS),
];

const authProviders: (Provider | EnvironmentProviders)[] = [
    provideAuth0({
        domain: environment.auth0Domain,
        clientId: environment.auth0ClientId,
        authorizationParams: {
            redirect_uri: window.location.origin,
            audience: environment.auth0Audience,
            scope: environment.scopes.quipexApi.join(' '),
        },
        httpInterceptor: {
            allowedList: [
                {
                    uri: `${environment.apiBase}/*`,
                    tokenOptions: buildTokenOptions(environment),
                },
                {
                    uri: `${environment.apiFunctionBase}/*`,
                    tokenOptions: buildTokenOptions(environment),
                },
                {
                    uri: `${environment.apiAIBase}/*`,
                    tokenOptions: buildTokenOptions(environment),
                },
            ],
        },
    }),
    {
        provide: HTTP_INTERCEPTORS,
        useClass: ManageHttpInterceptor,
        multi: true,
    },
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(),
        provideRouter(
            appRoutes,
            withPreloading(SelectivePreloadingStrategy),
            withComponentInputBinding(),
            withExperimentalAutoCleanupInjectors(),
            withRouterConfig({ paramsInheritanceStrategy: 'always' })
        ),
        provideHttpClient(
            withInterceptors([apiBaseUrlInterceptor, authHttpInterceptorFn]),
            withInterceptorsFromDi()
        ),
        provideAnimations(),
        provideNgIdle(),
        {
            provide: TitleStrategy,
            useClass: QuipexTitleStrategy,
        },
        {
            provide: APP_CONFIG_TOKEN,
            useFactory: () =>
                buildUiProfileConfig(
                    environment,
                    import.meta.env.NG_APP_UI_PROFILE,
                    productionFeatureFlags
                ),
        },
        provideAppInitializer(
            validateEnvironmentAndInitialiseAppFn(environment)
        ),
        ...authProviders,
        ...matAngularConfig,
        MonitoringService,
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
    ],
};

function buildTokenOptions(environment: Environment) {
    return {
        authorizationParams: {
            audience: environment.auth0Audience,
            scope: environment.scopes.quipexApi.join(' '),
        },
    };
}
